import { z } from 'zod';
import { attributeStorageSchema, attributeTypeSchema } from './enums.js';
/**
 * A field key. Stable, machine-readable, used as the JSONB property name and as
 * an export column identifier. Lower snake_case keeps it usable everywhere
 * without quoting.
 */
export const attributeKeySchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'Key must start with a lowercase letter and contain only lowercase letters, digits and underscores');
/**
 * Per-type validation rules, stored as JSONB on the definition.
 *
 * Modelled as a discriminated union so the designer cannot persist a nonsensical
 * combination such as a maximum length on a boolean. The iPhone app reads the
 * same rules to validate before anything is queued for sync.
 */
export const attributeValidationSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('text'),
        minLength: z.number().int().min(0).optional(),
        maxLength: z.number().int().min(1).max(4000).optional(),
        pattern: z.string().max(500).optional(),
        patternMessage: z.string().max(200).optional(),
    }),
    z.object({
        type: z.literal('longtext'),
        minLength: z.number().int().min(0).optional(),
        maxLength: z.number().int().min(1).max(100_000).optional(),
    }),
    z.object({
        type: z.literal('number'),
        min: z.number().optional(),
        max: z.number().optional(),
    }),
    z.object({
        type: z.literal('decimal'),
        min: z.number().optional(),
        max: z.number().optional(),
        /** Total number of significant digits. */
        precision: z.number().int().min(1).max(38).default(12),
        /** Digits after the decimal separator. A price field uses 2. */
        scale: z.number().int().min(0).max(10).default(2),
    }),
    z.object({
        type: z.literal('integer'),
        min: z.number().int().optional(),
        max: z.number().int().optional(),
    }),
    z.object({ type: z.literal('boolean') }),
    z.object({
        type: z.literal('date'),
        /** ISO date (YYYY-MM-DD). */
        min: z.iso.date().optional(),
        max: z.iso.date().optional(),
    }),
    z.object({
        type: z.literal('datetime'),
        min: z.iso.datetime({ offset: true }).optional(),
        max: z.iso.datetime({ offset: true }).optional(),
    }),
    z.object({ type: z.literal('select') }),
    z.object({
        type: z.literal('multiselect'),
        minItems: z.number().int().min(0).optional(),
        maxItems: z.number().int().min(1).optional(),
    }),
    z.object({
        type: z.literal('barcode'),
        /** Empty means: accept every symbology the scanner supports. */
        symbologies: z.array(z.string().min(1).max(32)).optional(),
        pattern: z.string().max(500).optional(),
    }),
    z.object({
        type: z.literal('photo'),
        minCount: z.number().int().min(0).max(50).optional(),
        maxCount: z.number().int().min(1).max(50).optional(),
    }),
    z.object({ type: z.literal('user') }),
]);
/**
 * A single entry in the field catalogue.
 *
 * There is exactly one catalogue. It drives the attribute designer, the iPhone
 * capture form and the export column picker alike — a field cannot exist in one
 * of those and be missing from another.
 */
export const attributeDefinitionSchema = z
    .object({
    id: z.uuid(),
    key: attributeKeySchema,
    label: z.string().min(1).max(200),
    type: attributeTypeSchema,
    /**
     * System fields live in the same catalogue but are protected: label,
     * helpText, group, sortOrder and showInList stay editable, while key, type
     * and required are frozen and deletion is blocked.
     */
    system: z.boolean(),
    /** Filled by the device or server, never by the user (item_id, created_at, created_by). */
    auto: z.boolean(),
    required: z.boolean(),
    /** Pre-fills this field on the next new item from the previously captured value. */
    carryOver: z.boolean(),
    helpText: z.string().max(1000).nullable(),
    group: z.string().max(100).nullable(),
    sortOrder: z.number().int(),
    showInList: z.boolean(),
    /** Required for select and multiselect, forbidden otherwise. */
    valueListId: z.uuid().nullable(),
    validation: attributeValidationSchema,
    storage: attributeStorageSchema,
    /** Default for new items, as a JSON value matching the field type. */
    defaultValue: z.unknown().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
})
    .refine((def) => def.validation.type === def.type, {
    message: 'validation.type must match the field type',
    path: ['validation', 'type'],
})
    .refine((def) => def.type === 'select' || def.type === 'multiselect'
    ? def.valueListId !== null
    : def.valueListId === null, {
    message: 'valueListId is required for select and multiselect, and must be null otherwise',
    path: ['valueListId'],
})
    .refine((def) => !(def.auto && !def.system), {
    message: 'Only system fields can be auto-filled',
    path: ['auto'],
})
    .refine((def) => (def.storage === 'relation') === (def.type === 'photo'), {
    message: 'Relation storage is used by photo fields, and only by those',
    path: ['storage'],
});
/**
 * Fields an admin may change on a *system* field. Everything else is frozen so
 * that sync, duplicate detection and export keep working.
 */
export const SYSTEM_FIELD_EDITABLE_PROPERTIES = [
    'label',
    'helpText',
    'group',
    'sortOrder',
    'showInList',
];
export const systemAttributeUpdateSchema = z.object({
    label: z.string().min(1).max(200).optional(),
    helpText: z.string().max(1000).nullable().optional(),
    group: z.string().max(100).nullable().optional(),
    sortOrder: z.number().int().optional(),
    showInList: z.boolean().optional(),
});
/** Fields an admin may change on a non-system field: everything but the key. */
export const attributeUpdateSchema = systemAttributeUpdateSchema.extend({
    type: attributeTypeSchema.optional(),
    required: z.boolean().optional(),
    carryOver: z.boolean().optional(),
    valueListId: z.uuid().nullable().optional(),
    validation: attributeValidationSchema.optional(),
    defaultValue: z.unknown().nullable().optional(),
});
export const attributeCreateSchema = z.object({
    key: attributeKeySchema,
    label: z.string().min(1).max(200),
    type: attributeTypeSchema,
    required: z.boolean().default(false),
    carryOver: z.boolean().default(false),
    helpText: z.string().max(1000).nullable().default(null),
    group: z.string().max(100).nullable().default(null),
    sortOrder: z.number().int().default(0),
    showInList: z.boolean().default(false),
    valueListId: z.uuid().nullable().default(null),
    validation: attributeValidationSchema,
    defaultValue: z.unknown().nullable().default(null),
});
/**
 * Builds the Zod schema that validates one captured value against its
 * definition. This is what turns a row in the catalogue into an enforceable
 * rule, and it is deliberately the only place that knows how each type behaves.
 */
export function valueSchemaFor(def) {
    const v = def.validation;
    let schema;
    switch (v.type) {
        case 'text':
        case 'longtext': {
            let s = z.string();
            if (v.minLength !== undefined)
                s = s.min(v.minLength);
            if (v.maxLength !== undefined)
                s = s.max(v.maxLength);
            if (v.type === 'text' && v.pattern !== undefined) {
                s = s.regex(new RegExp(v.pattern), v.patternMessage);
            }
            schema = s;
            break;
        }
        case 'number':
        case 'decimal': {
            // z.number() already rejects Infinity and NaN in Zod 4.
            let s = z.number();
            if (v.min !== undefined)
                s = s.min(v.min);
            if (v.max !== undefined)
                s = s.max(v.max);
            if (v.type === 'decimal') {
                const { scale } = v;
                schema = s.refine((n) => {
                    const decimals = (n.toString().split('.')[1] ?? '').length;
                    return decimals <= scale;
                }, { message: `At most ${scale} decimal places allowed` });
                break;
            }
            schema = s;
            break;
        }
        case 'integer': {
            let s = z.number().int();
            if (v.min !== undefined)
                s = s.min(v.min);
            if (v.max !== undefined)
                s = s.max(v.max);
            schema = s;
            break;
        }
        case 'boolean':
            schema = z.boolean();
            break;
        case 'date': {
            const s = z.iso.date();
            schema = s.refine((d) => (v.min === undefined || d >= v.min) && (v.max === undefined || d <= v.max), { message: 'Date outside the allowed range' });
            break;
        }
        case 'datetime': {
            const s = z.iso.datetime({ offset: true });
            schema = s.refine((d) => (v.min === undefined || Date.parse(d) >= Date.parse(v.min)) &&
                (v.max === undefined || Date.parse(d) <= Date.parse(v.max)), { message: 'Timestamp outside the allowed range' });
            break;
        }
        case 'select':
        case 'user':
            // Referential validity is checked against the database, not here.
            schema = z.uuid();
            break;
        case 'multiselect': {
            let s = z.array(z.uuid());
            if (v.minItems !== undefined)
                s = s.min(v.minItems);
            if (v.maxItems !== undefined)
                s = s.max(v.maxItems);
            schema = s;
            break;
        }
        case 'barcode': {
            let s = z.string().min(1).max(200);
            if (v.pattern !== undefined)
                s = s.regex(new RegExp(v.pattern));
            schema = s;
            break;
        }
        case 'photo': {
            let s = z.array(z.uuid());
            if (v.minCount !== undefined)
                s = s.min(v.minCount);
            if (v.maxCount !== undefined)
                s = s.max(v.maxCount);
            schema = s;
            break;
        }
    }
    return def.required ? schema : schema.nullable().optional();
}
//# sourceMappingURL=attribute.js.map